import React from "react";

import NavigationBarLink from "./navigationbar.link";
import NavigationBarSubMenu from "./navigationbar.submenu";
import NavigationBarSubMenuWithLink from "./navigationbar.submenu.with.link";

import Icon from "src/components/icon";
import Accordion from "src/components/accordion";

import { getLanguageField } from "src/utils/language";
import { isRunTime } from "src/utils/isRunTime";
import FlyoutPromoBoxV1 from "src/modules/flyoutPromoBox/flyout.promo.box.v1";

type Props = {
    depth: number;
    language: string;
    defaultLanguage: string;
    item: TNavigationItem;
    chevrons_on_menus: boolean;
    flyout_menu_opening: TFlyoutMenuOpening;
    collapse_first_level_items: boolean;
    item_type: string;
    layout_orientation: "horizontal" | "vertical";
    number_of_columns: 2 | 3 | 4;
}

type State = {
    menuOpen: boolean;
    minWidth: number;
    offsetLeft: number;
}

export default class NavigationBarMenu extends React.Component<Props, State> {

    public accordionRef = React.createRef<Accordion>();
    public popupRef = React.createRef<HTMLDivElement>();

    public state: State = {
        menuOpen: false,
        minWidth: 200,
        offsetLeft: 0,
    };

    public get label(): string {
        return getLanguageField(this.props.item.translations, this.props.language, 'label') as string;
    }

    onClick = () => {
        if (this.accordionRef.current) this.accordionRef.current.close();
    }

    recalc = () => {
        if (!this.popupRef.current || !this.accordionRef.current) return;
        if (!isRunTime()) return;

        // button position
        const triggerRect = this.accordionRef.current.getBoundingClientRect();
        // dropdown position
        const dropdownRect = this.popupRef.current.getBoundingClientRect();
        // viewport width
        const viewportWidth = window.innerWidth;

        let offsetLeft = 0;

        // calculate right position to check if it overflows (initial position + width - viewport width)
        const rightOverflow = (triggerRect.left + dropdownRect.width) - viewportWidth;
        // calculate left position to check if it overflows
        const leftOverflow = triggerRect.left - dropdownRect.width;

        // a value greater than 0 means that the dropdown will overflow to the right
        if (rightOverflow > 0) {
            offsetLeft = -rightOverflow - 20; // move to the left all the overflow plus 20px
        } else if (leftOverflow < 0 && triggerRect.left < dropdownRect.width) {
            // if the dropdown is wider than the button, move to the left all the overflow plus 10px
            offsetLeft = -leftOverflow - 10;
        }

        this.setState({ offsetLeft });
    }

    private observer: ResizeObserver | undefined = isRunTime() && typeof window.ResizeObserver !== "undefined" ? new ResizeObserver(this.recalc) : undefined;

    onDimension = (width: number) => {
        const maxW = Math.max(this.state.minWidth, width);
        const limW = Math.min(maxW, 400);
        this.setState({minWidth: limW});
    }

    public get color() {
        if (this.state.menuOpen) return "text-[color:var(--hover-color)] hover:text-[color:var(--hover-color)]";
        return "text-[color:var(--text-color)] hover:text-[color:var(--hover-color)]";
    }

    public get width() {
        if (this.props.layout_orientation === "horizontal") {
            return { width: 'max-content' };
        }
        return { minWidth: this.state.minWidth };
    }

    public get left() {
        return { left: this.state.offsetLeft };
    }

    get flyoutMenuOpeningisHover(){
        const { flyout_menu_opening } = this.props;
        return flyout_menu_opening === "hover";
    }

    componentDidMount(): void {
        if (this.popupRef.current && this.observer) {
            this.observer.observe(this.popupRef.current);
        } else {
            setTimeout(() => this.recalc, 100);
        }
    }
    componentWillUnmount(): void {
        if (this.observer) this.observer.disconnect();
    }

    renderWithoutItems(): React.ReactNode {
        return <div style={this.style} className={`flex flex-row items-center navBarText ${this.color}`}>
            <span className="truncate">{this.label}</span>
        </div>
    }

    renderMenuLabel(): React.ReactNode {
        if (this.props.item_type === "Menu with link") {
            return (
                <div style={this.style} className={`flex flex-row items-center cursor-pointer navBarText ${this.color}`}>
                    <NavigationBarLink 
                        depth={this.props.depth}
                        language={this.props.language}
                        defaultLanguage={this.props.defaultLanguage}
                        item={this.props.item}
                        onClick={this.onClick}
                    />
                    {this.props.chevrons_on_menus ? <Icon icon="ChevronDown" className={`ml-1 ${this.state.menuOpen ? 'rotate-180' : ''}`} /> : null}
                </div>
            );
        }
        
        return <div style={this.style} className={`flex flex-row items-center cursor-pointer navBarText ${this.color}`}>
            <span className="truncate">{this.label}</span> {this.props.chevrons_on_menus ? <Icon icon="ChevronDown" className={`ml-1 ${this.state.menuOpen ? 'rotate-180' : ''}`} /> : null}
        </div>
    }

    renderAccordionComponent(){
        if (this.flyoutMenuOpeningisHover) return (
            <div className={`group mt-1 ${this.props.layout_orientation === "horizontal" ? "" : "relative"}`}>
                <div className="pb-1">
                    {this.renderMenuLabel()}
                </div>
                <div className="hidden group-hover:flex">
                    {this.renderChildren()}
                </div>
            </div>
        )
        return (
            <Accordion
                ref={this.accordionRef}
                closeOther={true}
                opener={this.renderMenuLabel()}
                removeChildrenWhenClosed={true}
                onToggle={(open) => this.setState({menuOpen: open}, this.recalc)}
            >
                {this.renderChildren()}
            </Accordion>
        )
    }

    private get itemsContainerClassName(): string {
        const { layout_orientation, number_of_columns } = this.props;
        if (layout_orientation === "horizontal") {
            // On smaller screens (below lg), cap at 3 columns. On lg+, use configured columns
            let gridCols: string;
            if (number_of_columns === 2) {
                gridCols = "grid-cols-2";
            } else if (number_of_columns === 3) {
                gridCols = "grid-cols-3";
            } else { // 4 columns
                gridCols = "grid-cols-3 xl:grid-cols-4"; // Reduce to 3 on smaller screens
            }
            return `grid ${gridCols} gap-x-4 gap-y-6 items-start justify-start horizontal-orientation`;
        }
        return "flex flex-col justify-start gap-4";
    }

    renderChildren(){
        const { defaultLanguage, language, collapse_first_level_items,layout_orientation } = this.props;

        return <div className="absolute z-50" style={this.left}>
            <div ref={this.popupRef} className="p-6 flex flex-row items-start shadow-md rounded-lg max-h-[80vh] overflow-y-auto border border-[color:var(--dropdown-outline)] bg-[color:var(--dropdown-background)]">
                <div className={this.itemsContainerClassName} style={this.width}>
                    {this.items.map((item, idx) => {
                        if (item.item_type === "Link") return <NavigationBarLink onClick={this.onClick} onDimension={this.onDimension} depth={this.props.depth + 1} language={language} defaultLanguage={defaultLanguage} key={`navbar_link_${idx}`} item={item} />
                        if (item.item_type === "Menu") return <NavigationBarSubMenu collapse_first_level_items={layout_orientation === "horizontal" ? false : collapse_first_level_items} onClick={this.onClick} onDimension={this.onDimension} depth={this.props.depth + 1} language={language} defaultLanguage={defaultLanguage} key={`navbar_menu_${idx}`} item={item} />
                        if (item.item_type === "Menu with link") return <NavigationBarSubMenuWithLink collapse_first_level_items={layout_orientation === "horizontal" ? false : collapse_first_level_items} onClick={this.onClick} onDimension={this.onDimension} depth={this.props.depth + 1} language={language} defaultLanguage={defaultLanguage} key={`navbar_menu_${idx}`} item={item} />
                        return null;
                    })}
                </div>
                {this.renderFlyoutPromoBox()}
            </div>
        </div>
    }

    private get style(): any {
        return {
            '--text-color': this.props.item.item_color?.value,
            '--hover-color': this.props.item.item_hover_color?.value,
        }
    }

    private get items(){
        return (this.props.item?.items || []).filter(item => item.hide !== "yes")
    }

    renderFlyoutPromoBox() {
        if (!this.props.item.flyout_promo_box) return null;
        const openerWithProps = React.Children.map(
            [<FlyoutPromoBoxV1 flyoutPromoBox={this.props.item.flyout_promo_box} language={this.props.language} defaultLanguage={this.props.defaultLanguage}/>], child => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child, {
                closeNavigation: () => {
                    if (this.accordionRef.current) this.accordionRef.current.close();
                    this.setState({menuOpen: false})
                }} as any);
            }
            return child;
        });

        return openerWithProps;
    }

    render(): React.ReactNode {
        // If no items, render placeholder:
        if ((this.props.item.items || []).length === 0) return this.renderWithoutItems();

        return <>
            <div className={`${this.props.layout_orientation === "horizontal" ? "" : "relative"}`}>
                {this.renderAccordionComponent()}
            </div>
        </>;
    }
}